'use client';

import { useState, useEffect, useRef, useCallback } from 'react';

interface BeforeAfterSliderProps {
  beforeSrc: string;
  afterSrc: string;
  poster?: string;
  className?: string;
}

export function BeforeAfterSlider({
  beforeSrc,
  afterSrc,
  poster,
  className = 'relative w-full aspect-video rounded-2xl overflow-hidden select-none shadow-2xl border border-white/10',
}: BeforeAfterSliderProps) {
  const [sliderPos, setSliderPos] = useState(50); // 0-100 %
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const [hintDone, setHintDone] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const beforeVideoRef = useRef<HTMLVideoElement>(null);
  const afterVideoRef = useRef<HTMLVideoElement>(null);

  // Lazy load component when it enters viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsLoaded(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' },
    );
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, []);

  // Guarantee muted attribute is set directly on the DOM nodes (Safari autoplay fix)
  // and trigger play programmatically.
  useEffect(() => {
    if (!isLoaded) return;

    const playVideo = (videoEl: HTMLVideoElement | null) => {
      if (!videoEl) return;
      videoEl.muted = true;
      videoEl.defaultMuted = true;

      const playPromise = videoEl.play();
      if (playPromise !== undefined) {
        playPromise.catch((err) => {
          console.log('Autoplay prevented programmatically: ', err);
        });
      }
    };

    // Attempt play on load
    playVideo(beforeVideoRef.current);
    playVideo(afterVideoRef.current);
  }, [isLoaded]);

  // Backup play trigger on user interaction (e.g. hovering or sliding)
  // to bypass battery saver/user gesture blocks.
  const handleUserInteraction = useCallback(() => {
    if (!isLoaded) return;
    const playVideo = (videoEl: HTMLVideoElement | null) => {
      if (videoEl && videoEl.paused) {
        videoEl.play().catch(() => {});
      }
    };
    playVideo(beforeVideoRef.current);
    playVideo(afterVideoRef.current);
  }, [isLoaded]);

  // Compute position from a clientX event
  const updatePos = useCallback(
    (clientX: number) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = Math.max(0, Math.min((clientX - rect.left) / rect.width, 1)) * 100;
      setSliderPos(pct);
      handleUserInteraction();
    },
    [handleUserInteraction],
  );

  // Global mouse / touch listeners so drag doesn't break when cursor leaves container
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (isDragging.current) updatePos(e.clientX);
    };
    const onTouchMove = (e: TouchEvent) => {
      if (isDragging.current) updatePos(e.touches[0].clientX);
    };
    const onUp = () => {
      isDragging.current = false;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [updatePos]);

  // One-shot hint animation: sweep left then back to center
  useEffect(() => {
    let raf: number;
    const sweep = (from: number, to: number, duration: number, onDone?: () => void) => {
      const start = performance.now();
      const tick = (now: number) => {
        const t = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        setSliderPos(from + (to - from) * eased);
        if (t < 1) {
          raf = requestAnimationFrame(tick);
        } else {
          onDone?.();
        }
      };
      raf = requestAnimationFrame(tick);
    };

    const t1 = setTimeout(() => {
      sweep(50, 20, 800, () => {
        const t2 = setTimeout(() => {
          sweep(20, 50, 700, () => setHintDone(true));
        }, 400);
        return () => clearTimeout(t2);
      });
    }, 900);

    return () => {
      clearTimeout(t1);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ cursor: 'ew-resize' }}
      onMouseDown={(e) => {
        isDragging.current = true;
        updatePos(e.clientX);
      }}
      onTouchStart={(e) => {
        isDragging.current = true;
        updatePos(e.touches[0].clientX);
      }}
      onMouseEnter={handleUserInteraction}
    >
      {/* ── BEFORE video (always full-size underneath) ── */}
      {!isLoaded ? (
        poster && (
          <img
            src={poster}
            alt="Before Preview"
            className="absolute inset-0 w-full h-full object-cover bg-zinc-900"
            loading="lazy"
          />
        )
      ) : (
        <video
          ref={beforeVideoRef}
          src={beforeSrc}
          autoPlay
          loop
          playsInline
          muted
          preload="auto"
          poster={poster}
          className="absolute inset-0 w-full h-full object-contain bg-zinc-900"
          controlsList="nodownload"
          onLoadedData={() => {
            if (beforeVideoRef.current) {
              beforeVideoRef.current.play().catch(() => {});
            }
          }}
        />
      )}

      {/* ── AFTER video clipped to the left of the divider ── */}
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}>
        {!isLoaded ? (
          poster && (
            <img
              src={poster}
              alt="After Preview"
              className="absolute inset-0 w-full h-full object-cover bg-zinc-900"
              loading="lazy"
            />
          )
        ) : (
          <video
            ref={afterVideoRef}
            src={afterSrc}
            autoPlay
            loop
            playsInline
            muted
            preload="auto"
            poster={poster}
            className="absolute inset-0 w-full h-full object-contain bg-zinc-900"
            controlsList="nodownload"
            onLoadedData={() => {
              if (afterVideoRef.current) {
                afterVideoRef.current.play().catch(() => {});
              }
            }}
          />
        )}
        {/* green tint edge */}
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/20 via-transparent to-transparent pointer-events-none" />
      </div>

      {/* ── Divider line + drag handle ── */}
      <div
        className="absolute top-0 bottom-0 z-20 flex flex-col items-center pointer-events-none"
        style={{ left: `${sliderPos}%`, transform: 'translateX(-50%)' }}
      >
        {/* Glowing line */}
        <div
          className="w-0.5 h-full bg-white/90"
          style={{ boxShadow: '0 0 8px 1px rgba(255,255,255,0.55)' }}
        />

        {/* Handle circle */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white flex items-center justify-center pointer-events-auto"
          style={{
            boxShadow: '0 0 0 3px rgba(139,92,246,0.5), 0 4px 24px rgba(0,0,0,0.45)',
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            isDragging.current = true;
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            isDragging.current = true;
          }}
        >
          {/* Double-chevron arrows */}
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path
              d="M8 6L3 11L8 16"
              stroke="#7C3AED"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M14 6L19 11L14 16"
              stroke="#7C3AED"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/* ── Labels ── */}
      {/* BEFORE — right side */}
      <div
        className="absolute top-4 right-4 z-30 flex items-center gap-2 bg-zinc-900/80 backdrop-blur-sm border border-red-500/40 px-3 py-1.5 rounded-full pointer-events-none transition-opacity duration-300"
        style={{ opacity: sliderPos < 90 ? 1 : 0 }}
      >
        <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
        <span className="text-red-300 text-xs font-bold tracking-widest uppercase">Before</span>
      </div>
      {/* AFTER — left side */}
      <div
        className="absolute top-4 left-4 z-30 flex items-center gap-2 bg-zinc-900/80 backdrop-blur-sm border border-emerald-500/40 px-3 py-1.5 rounded-full pointer-events-none transition-opacity duration-300"
        style={{ opacity: sliderPos > 10 ? 1 : 0 }}
      >
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-emerald-300 text-xs font-bold tracking-widest uppercase">After</span>
      </div>

      {/* ── Hint tooltip ── */}
      <div
        className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-zinc-900/70 backdrop-blur-sm px-4 py-2 rounded-full border border-white/10 pointer-events-none transition-opacity duration-700"
        style={{ opacity: hintDone ? 0 : 0.85 }}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M5 3L2 7L5 11M9 3L12 7L9 11"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.7"
          />
        </svg>
        <span className="text-white/70 text-xs font-medium">Drag to compare</span>
      </div>
    </div>
  );
}
